import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DtoFilterValues,
  DtoReferenceCategory,
  DtoReferencePageInfo,
  Language,
  ReferenceCategoryType,
  ReferenceDocumentType,
  ReferenceDocumentTypeDto
} from '@common';
import { Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ReferencesService {
  //#region Private Fields ----------------------------------------------------
  private readonly httpClient: HttpClient;
  private readonly pdfReferenceRoot: string;
  private readonly filterValuesCache: Map<Language, DtoFilterValues>;
  private supportedCategories: Array<DtoReferenceCategory> | null;
  private documentTypes: Array<ReferenceDocumentTypeDto> | null;
  //#endregion

  //#region Constructor & C° --------------------------------------------------
  public constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
    this.pdfReferenceRoot = 'api/pdf/references/';
    this.filterValuesCache = new Map<Language, DtoFilterValues>();
    this.supportedCategories = null;
    this.documentTypes = null;
  }
  //#endregion

  //#region Public Methods ----------------------------------------------------
  public buildPdfLink(
    language: Language,
    infoPage: ReferenceCategoryType | null,
    brand: string,
    documentType: ReferenceDocumentType,
    idList: Array<string>
  ): string | null {
    if (idList.length > 0) {
      const now = new Date();

      let result = `${this.pdfReferenceRoot}custom/generate/`;
      result += `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}-`;
      result += language == Language.de ? 'referenzen' : 'references';
      result += `-${language}?language=${language}`;
      result += `&brand=${brand}`;
      result += `&ids=${idList.join(',')}`;
      if (infoPage != null) {
        result += `&info=${infoPage}`;
      }
      if (documentType == 'one-pagers') {
        result += '&op=true';
      }
      return result;
    } else {
      return null;
    }
  }

  public fetchAvailablePages(
    language: Language,
    categories?: Array<ReferenceCategoryType>,
    customers?: Array<string>,
    roles?: Array<string>,
    keywords?: Array<string>
  ): Observable<Array<DtoReferencePageInfo>> {
    const query = new Array<string>();
    const languageToUse = language == Language.de ? language : Language.en;
    if (categories) {
      query.push(`cat=${categories.join(',')}`);
    }
    if (customers) {
      query.push(`cust=${customers.join(',')}`);
    }
    if (keywords) {
      query.push(`kw=${keywords.join(',')}`);
    }
    if (roles) {
      query.push(`role=${roles.join(',')}`);
    }
    const url =
      query.length > 0
        ? `${this.pdfReferenceRoot}pages/${languageToUse}?${query.join('&')}`
        : `${this.pdfReferenceRoot}pages/${languageToUse}`;
    return this.httpClient.get<Array<DtoReferencePageInfo>>(url);
  }

  public fetchFilterValues(language: Language): Observable<DtoFilterValues> {
    const languageToUse = language == Language.de ? language : Language.en;
    if (this.filterValuesCache.has(languageToUse)) {
      return of(this.filterValuesCache.get(languageToUse));
    } else {
      return this.httpClient
        .get<DtoFilterValues>(`${this.pdfReferenceRoot}filter-values/${languageToUse}`)
        .pipe(tap((resp: DtoFilterValues) => this.filterValuesCache.set(languageToUse, resp)));
    }
  }

  public fetchSupportedCategories(): Observable<Array<DtoReferenceCategory>> {
    if (this.supportedCategories === null) {
      return this.httpClient
        .get<Array<DtoReferenceCategory>>(`${this.pdfReferenceRoot}custom/supported-categories`)
        .pipe(tap((resp: Array<DtoReferenceCategory>) => (this.supportedCategories = resp)));
    } else {
      return of(this.supportedCategories);
    }
  }

  public fetchReferenceDocumentTypes(): Observable<Array<ReferenceDocumentTypeDto>> {
    if (this.documentTypes === null) {
      return this.httpClient
        .get<Array<ReferenceDocumentTypeDto>>(`${this.pdfReferenceRoot}custom/document-types`)
        .pipe(tap((resp: Array<ReferenceDocumentTypeDto>) => (this.documentTypes = resp)));
    } else {
      return of(this.documentTypes);
    }
  }

  //#endregion
}
